/**
 * AdminMcpKeysPage - Super admin page for managing system-level MCP keys
 *
 * Features:
 * - Stats cards: Active, Recently Used, Revoked
 * - DataTable with system MCP keys
 * - Create modal with system scopes
 * - Revoke with reason
 * - Config examples section
 */

import { useState, useEffect, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, Button, Badge, Column, Modal, Input, Label, Spinner } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import {
  Plus,
  Loader2,
  Bot,
  Shield,
  Trash2,
  Copy,
  Check,
  Clock,
  AlertCircle,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useUserTimezone } from '@/hooks/useDateFormat';
import { McpConfigExamples } from '@/components/mcp-keys';
import { Checkbox } from '@/components/ui/Checkbox';
import type {
  SystemApiKeyScope,
  RateLimitTier,
} from '@simcheck/shared-types';
import {
  listSystemMcpKeys,
  createSystemMcpKey,
  revokeSystemMcpKey,
  McpKeyListItem,
} from '@/services/mcpKeyService';

// System scope definitions for display
const SYSTEM_SCOPES: { scope: SystemApiKeyScope; displayName: string; description: string }[] = [
  { scope: 'system:read_all_orgs', displayName: 'Read All Orgs', description: 'View all organizations in the platform' },
  { scope: 'system:read_all_executions', displayName: 'Read All Executions', description: 'View test executions across all organizations' },
  { scope: 'system:read_all_users', displayName: 'Read All Users', description: 'View all users in the platform' },
  { scope: 'system:read_metrics', displayName: 'Read Metrics', description: 'View platform-wide metrics and analytics' },
  { scope: 'system:read_audit_logs', displayName: 'Read Audit Logs', description: 'View all audit logs across the platform' },
  { scope: 'system:health_check', displayName: 'Health Check', description: 'Access health check endpoints' },
  { scope: 'system:status', displayName: 'System Status', description: 'View system status and diagnostics' },
  { scope: 'system:manage_locks', displayName: 'Manage Locks', description: 'Manage resource locks across the platform' },
  { scope: 'system:manage_queue', displayName: 'Manage Queue', description: 'Manage execution queue operations' },
  { scope: 'system:manage_settings', displayName: 'Manage Settings', description: 'Read and update system settings' },
  { scope: 'system:*', displayName: 'Full System Access', description: 'Complete access to all system operations (super admin equivalent)' },
];

const RATE_LIMIT_TIERS: { value: RateLimitTier; label: string; description: string }[] = [
  { value: 'standard', label: 'Standard', description: '60/min, 1K/hr, 10K/day' },
  { value: 'elevated', label: 'Elevated', description: '300/min, 5K/hr, 50K/day' },
  { value: 'unlimited', label: 'Unlimited', description: 'No rate limits' },
];

// Format date for display
function formatDate(date: Date | string | undefined, timeZone?: string): string {
  if (!date) return '-';
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  });
}

// Format relative time
function formatRelativeTime(date: Date | string | undefined): string {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 2592000000) return `${Math.floor(diff / 86400000)}d ago`;
  return formatDate(d);
}

export function AdminMcpKeysPage() {
  const { isSuperAdmin } = useAuth();
  const { success, error: showError } = useToast();
  const tz = useUserTimezone();

  // Data state
  const [mcpKeys, setMcpKeys] = useState<McpKeyListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<McpKeyListItem | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Create form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formScopes, setFormScopes] = useState<SystemApiKeyScope[]>([]);
  const [formRateLimitTier, setFormRateLimitTier] = useState<RateLimitTier>('unlimited');
  const [formAllowedIPs, setFormAllowedIPs] = useState('');

  // Revoke form state
  const [revokeReason, setRevokeReason] = useState('');

  // Created key display
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showCreatedKey, setShowCreatedKey] = useState(true);
  const [copiedKey, setCopiedKey] = useState(false);

  // Fetch MCP keys
  const fetchMcpKeys = useCallback(async () => {
    try {
      const response = await listSystemMcpKeys();
      setMcpKeys(response.mcpKeys || []);
    } catch (err: any) {
      console.error('Error fetching system MCP keys:', err);
      showError(err.message || 'Failed to load system MCP keys');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchMcpKeys();
  }, [fetchMcpKeys]);

  // Reset create form
  const resetCreateForm = () => {
    setFormName('');
    setFormDescription('');
    setFormScopes([]);
    setFormRateLimitTier('unlimited');
    setFormAllowedIPs('');
    setCreatedKey(null);
    setShowCreatedKey(true);
    setCopiedKey(false);
  };

  // Toggle scope selection
  const toggleScope = (scope: SystemApiKeyScope) => {
    setFormScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  // Select all scopes
  const selectAllScopes = () => {
    setFormScopes(['system:*']);
  };

  // Handle create submit
  const handleCreate = async () => {
    if (!formName.trim()) {
      showError('Please provide a name for the MCP key');
      return;
    }

    if (formScopes.length === 0) {
      showError('Please select at least one scope');
      return;
    }

    setFormSubmitting(true);
    try {
      const response = await createSystemMcpKey({
        name: formName.trim(),
        description: formDescription.trim() || undefined,
        systemScopes: formScopes,
        rateLimitTier: formRateLimitTier,
        allowedIPs: formAllowedIPs.trim()
          ? formAllowedIPs.split(',').map((ip) => ip.trim()).filter(Boolean)
          : undefined,
      });

      // Show the created key (only shown once)
      setCreatedKey(response.key);

      success('System MCP key created successfully');
      fetchMcpKeys();
    } catch (err: any) {
      showError(err.message || 'Failed to create system MCP key');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Handle copy key
  const handleCopyKey = async () => {
    if (!createdKey) return;

    try {
      await navigator.clipboard.writeText(createdKey);
      setCopiedKey(true);
      success('MCP key copied to clipboard');
      setTimeout(() => setCopiedKey(false), 2000);
    } catch (err) {
      showError('Failed to copy key to clipboard');
    }
  };

  // Handle revoke
  const handleRevoke = async () => {
    if (!selectedKey) return;

    setFormSubmitting(true);
    try {
      await revokeSystemMcpKey(selectedKey.id, revokeReason.trim() || undefined);

      success('System MCP key revoked successfully');
      setShowRevokeModal(false);
      setSelectedKey(null);
      setRevokeReason('');
      fetchMcpKeys();
    } catch (err: any) {
      showError(err.message || 'Failed to revoke system MCP key');
    } finally {
      setFormSubmitting(false);
    }
  };

  // Table columns
  const columns: Column<McpKeyListItem>[] = [
    {
      key: 'name',
      header: 'Key',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="font-semibold">{row.name}</div>
            <div className="text-xs text-muted-foreground font-mono">{row.keyPrefix}...</div>
          </div>
        </div>
      ),
    },
    {
      key: 'systemScopes',
      header: 'Scopes',
      sortable: false,
      render: (_value: unknown, row: McpKeyListItem) => {
        const scopes = row.systemScopes || [];
        const hasFullAccess = scopes.includes('system:*');

        if (hasFullAccess) {
          return (
            <Badge className="bg-status-warning-bg text-status-warning">
              <Shield className="w-3 h-3 mr-1" />
              Full Access
            </Badge>
          );
        }

        return (
          <div className="flex flex-wrap gap-1">
            {scopes.slice(0, 2).map((scope) => (
              <Badge key={scope} variant="secondary" className="text-xs">
                {scope.replace('system:', '')}
              </Badge>
            ))}
            {scopes.length > 2 && (
              <Badge variant="secondary" className="text-xs">
                +{scopes.length - 2} more
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'rateLimitTier',
      header: 'Rate Limit',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <Badge variant="secondary">
          {row.rateLimitTier}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <Badge
          className={
            row.status === 'active'
              ? 'bg-status-success-bg text-status-success'
              : row.status === 'revoked'
              ? 'bg-status-error-bg text-status-error'
              : 'bg-status-warning-bg text-status-warning'
          }
        >
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'lastUsedAt',
      header: 'Last Used',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <div className="flex items-center gap-1 text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span className="text-sm">{formatRelativeTime(row.lastUsedAt)}</span>
        </div>
      ),
    },
    {
      key: 'usageCount',
      header: 'Usage',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <span className="font-mono text-sm">{row.usageCount.toLocaleString()}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <span className="text-sm text-muted-foreground">{formatDate(row.createdAt, tz)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      render: (_value: unknown, row: McpKeyListItem) => (
        <div className="flex gap-2">
          {row.status === 'active' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedKey(row);
                setShowRevokeModal(true);
              }}
              className="text-status-error hover:text-status-error"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  // Stats
  const activeKeys = mcpKeys.filter((k) => k.status === 'active');
  const revokedKeys = mcpKeys.filter((k) => k.status === 'revoked');
  const recentlyUsed = mcpKeys.filter((k) => {
    if (!k.lastUsedAt) return false;
    const lastUsed = new Date(k.lastUsedAt);
    const dayAgo = new Date(Date.now() - 86400000);
    return lastUsed > dayAgo;
  });

  if (!isSuperAdmin) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-status-error mx-auto mb-4" />
            <p className="text-muted-foreground">
              You do not have permission to access this page.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Loading system MCP keys...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-display font-semibold text-foreground mb-2">
              System MCP Keys
            </h1>
            <p className="text-body text-muted-foreground">
              Manage platform-wide MCP keys for AI assistant integrations
            </p>
          </div>

          <Button
            variant="default"
            onClick={() => {
              resetCreateForm();
              setShowCreateModal(true);
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Create System MCP Key
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Active Keys
                </div>
                <div className="text-display font-mono font-bold text-status-success">
                  {activeKeys.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-status-success/10 rounded flex items-center justify-center">
                <Bot className="w-6 h-6 text-status-success" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Recently Used
                </div>
                <div className="text-display font-mono font-bold text-primary">
                  {recentlyUsed.length}
                </div>
                <div className="text-xs text-muted-foreground">Last 24 hours</div>
              </div>
              <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                <Clock className="w-6 h-6 text-primary" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-caption text-muted-foreground uppercase mb-2">
                  Revoked Keys
                </div>
                <div className="text-display font-mono font-bold text-muted-foreground">
                  {revokedKeys.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-muted rounded flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-muted-foreground" />
              </div>
            </div>
          </Card>
        </div>

        {/* Keys Table */}
        <Card className="p-6">
          <DataTable
            columns={columns}
            data={mcpKeys}
            keyExtractor={(row) => row.id}
            paginated
            defaultPageSize={10}
            searchable
            searchPlaceholder="Search by name or key prefix..."
          />
        </Card>

        {/* Configuration Examples */}
        <Card className="p-6">
          <h3 className="text-h3 font-semibold text-foreground mb-4">
            Configuration Examples
          </h3>
          <McpConfigExamples />
        </Card>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetCreateForm();
        }}
        title={createdKey ? 'MCP Key Created' : 'Create System MCP Key'}
        description={
          createdKey
            ? 'Save this key now - it will never be shown again!'
            : 'Create a new platform-wide MCP key for AI assistant integration'
        }
        size="lg"
        footer={
          createdKey ? (
            <Button
              variant="default"
              onClick={() => {
                setShowCreateModal(false);
                resetCreateForm();
              }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowCreateModal(false);
                  resetCreateForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreate}
                disabled={formSubmitting}
              >
                {formSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
                Create Key
              </Button>
            </>
          )
        }
      >
        {createdKey ? (
          <div className="space-y-4">
            <div className="bg-status-warning-bg/30 border border-status-warning/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-status-warning mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Save this key now!</span>
              </div>
              <p className="text-sm text-muted-foreground">
                This is the only time you will see this MCP key. Copy it and store it securely.
              </p>
            </div>

            <div className="space-y-2">
              <Label>MCP Key</Label>
              <div className="flex gap-2">
                <div className="flex-1 font-mono text-sm bg-muted p-3 rounded-md break-all">
                  {showCreatedKey ? createdKey : '•'.repeat(48)}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreatedKey(!showCreatedKey)}
                >
                  {showCreatedKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyKey}
                >
                  {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="keyName">Name *</Label>
              <Input
                id="keyName"
                placeholder="e.g., AI Assistant Integration Key"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="keyDescription">Description</Label>
              <Input
                id="keyDescription"
                placeholder="Optional description of what this key is used for"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>System Scopes *</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={selectAllScopes}
                  className="text-xs"
                >
                  Select Full Access
                </Button>
              </div>
              <div className="border rounded-lg p-4 space-y-3 max-h-60 overflow-y-auto">
                {SYSTEM_SCOPES.map((scopeDef) => (
                  <label
                    key={scopeDef.scope}
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <Checkbox
                      checked={formScopes.includes(scopeDef.scope)}
                      onCheckedChange={() => toggleScope(scopeDef.scope)}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="font-medium text-sm">{scopeDef.displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        {scopeDef.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rateLimitTier">Rate Limit Tier</Label>
              <select
                id="rateLimitTier"
                className="w-full p-2 border rounded-md bg-background text-foreground"
                value={formRateLimitTier}
                onChange={(e) => setFormRateLimitTier(e.target.value as RateLimitTier)}
              >
                {RATE_LIMIT_TIERS.map((tier) => (
                  <option key={tier.value} value={tier.value}>
                    {tier.label} ({tier.description})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allowedIPs">Allowed IPs (optional)</Label>
              <Input
                id="allowedIPs"
                placeholder="Comma-separated list of allowed IP addresses"
                value={formAllowedIPs}
                onChange={(e) => setFormAllowedIPs(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to allow all IPs
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setSelectedKey(null);
          setRevokeReason('');
        }}
        title="Revoke System MCP Key"
        description={`Are you sure you want to revoke "${selectedKey?.name}"? This action cannot be undone.`}
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowRevokeModal(false);
                setSelectedKey(null);
                setRevokeReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={formSubmitting}
            >
              {formSubmitting ? <Spinner size="sm" className="mr-2" /> : null}
              Revoke Key
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-status-error-bg/30 border border-status-error/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-status-error mb-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-semibold">Warning</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Any AI assistants using this key will immediately lose access.
              This action cannot be undone.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="revokeReason">Reason (optional)</Label>
            <Input
              id="revokeReason"
              placeholder="Why is this key being revoked?"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}

export default AdminMcpKeysPage;
